import { DataEntryStepProps, typedMemo } from "src/types";
import { useDataEntryStep1 } from "./data-entry-step-1.hook";
import { Image } from "react-bootstrap";
import { CircleKo, CircleOk, Infogive } from "src/assets";
import { InputText } from "src/ui/widgets";
import { StrongTranslationString } from "src/ui/widgets/mixed-translation-string/mixed-translation-string";
import { GIVE_BASE_CODE, GIVE_PLUS_CODE } from "src/config";

export const DataEntryStep1 = typedMemo((props: DataEntryStepProps) => {
  const {
    formik: { values, errors, touched, handleChange, handleBlur },
    validationSchema,
  } = props;

  const { MIN_PERIOD, t, onSelectPackage } = useDataEntryStep1({
    formik: props.formik,
  });

  // Select the insurance coverage type (Base or Plus)
  const CoverageTypes = (
    <div>
      <div className="font-16 mb-3">{t("data_entry.coverage_type_title")}</div>

      <div
        className={`p-3 align-items-start mb-4 cursor-pointer d-flex flex-column ${
          values.coverageTypeRadios === GIVE_BASE_CODE
            ? "package-box-selected"
            : "white-box"
        }`}
        onClick={() => onSelectPackage(GIVE_BASE_CODE)}
      >
        <div>
          <div className="c-secondary text-uppercase font-16-bold">
            {t("data_entry_step_1.coverage_type_base_title")}
          </div>
          <div className="font-14">
            {t("data_entry_step_1.coverage_type_subtitle")}
          </div>
        </div>

        <div className="coverage-grid">
          <div className="item">
            <Image
              src={CircleOk}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_1")}</div>
          </div>
          <div className="item">
            <Image
              src={CircleOk}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_2")}</div>
          </div>
          <div className="item opacity-25">
            <Image
              src={CircleKo}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_3")}</div>
          </div>
          <div className="item opacity-25">
            <Image
              src={CircleKo}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_4")}</div>
          </div>
        </div>
      </div>

      <div
        className={`p-3 align-items-start mb-4 cursor-pointer d-flex flex-column ${
          values.coverageTypeRadios === GIVE_PLUS_CODE
            ? "package-box-selected"
            : "white-box"
        }`}
        onClick={() => onSelectPackage(GIVE_PLUS_CODE)}
      >
        <div>
          <div className="c-secondary text-uppercase font-16-bold">
            {t("data_entry_step_1.coverage_type_plus_title")}
          </div>
          <div className="font-14">
            {t("data_entry_step_1.coverage_type_subtitle")}
          </div>
        </div>

        <div className="coverage-grid">
          <div className="item">
            <Image
              src={CircleOk}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_1")}</div>
          </div>
          <div className="item">
            <Image
              src={CircleOk}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_2")}</div>
          </div>
          <div className="item">
            <Image
              src={CircleOk}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_3")}</div>
          </div>
          <div className="item">
            <Image
              src={CircleOk}
              width={24}
              height={24}
              className="filter-00686F margin-icon"
            />
            <div>{t("data_entry_step_1.coverage_type_4")}</div>
          </div>
        </div>
      </div>
    </div>
  );

  // Number of insured days
  const InsuredDaysNumber = (
    <>
      <div className="col-12 p-0 m-0">
        <InputText
          name="insuredDaysNumber"
          handleChange={handleChange}
          handleBlur={handleBlur}
          validationSchema={validationSchema}
          touched={touched.insuredDaysNumber}
          value={values.insuredDaysNumber}
          error={errors.insuredDaysNumber}
          type="number"
          min={MIN_PERIOD}
          label={t("data_entry_step_1.coverage_insured_days_number")}
          placeholder={t(
            "data_entry_step_1.coverage_insured_days_number_placeholder"
          )}
        />
      </div>
    </>
  );

  // Number of insured people
  const InsuredNumber = (
    <div className="col-12 p-0 m-0">
      <InputText
        name="insuredNumber"
        handleChange={handleChange}
        handleBlur={handleBlur}
        validationSchema={validationSchema}
        touched={touched.insuredNumber}
        value={values.insuredNumber}
        error={errors.insuredNumber}
        min={1}
        type="number"
        label={t("data_entry.coverage_insured_number")}
        placeholder={t("data_entry_step_1.coverage_insured_number_placeholder")}
      />
    </div>
  );

  // Main input section combining coverage and period information
  const InputsSection = (
    <div className="col-12 col-lg-6 p-0 mb-2 mb-md-1 font-14 pe-md-3">
      {CoverageTypes}
      <div className=" mb-2 mb-md-1 font-14">
        {InsuredNumber}
        {InsuredDaysNumber}
      </div>
    </div>
  );

  const LabelsSection = (
    <div className="col-12 col-lg-6 p-0 margin-info">
      <div className="white-box-info p-3">
        <div>
          <div className="font-16-bold">
            {t("data_entry_step_1.coverage_description_p1")}
          </div>
          <div className="font-16-bold">
            {t("data_entry_step_1.coverage_description_p2")}
          </div>
        </div>
        <div className="my-5">
          <div className="d-flex align-items-center mb-2">
            <div className="bullet-info col-1 me-3" />
            <div className="font-16">
              <StrongTranslationString i18nKey="data_entry_step_1.coverage_description_bullet_1" />
            </div>
          </div>
          <div className="d-flex align-items-center mb-2">
            <div className="bullet-info col-1 me-3" />
            <div className="font-16">
              <StrongTranslationString i18nKey="data_entry_step_1.coverage_description_bullet_2" />
            </div>
          </div>
          <div className="d-flex align-items-center mb-2">
            <div className="bullet-info col-1 me-3" />
            <div className="font-16">
              <StrongTranslationString i18nKey="data_entry_step_1.coverage_description_bullet_3" />
            </div>
          </div>
        </div>
        <div>
          <Image
            src={Infogive}
            alt="Itasgive"
            width="100%"
            height="100%"
            style={{ borderRadius: "8px" }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="d-lg-flex">
        {InputsSection}
        {LabelsSection}
      </div>
    </div>
  );
});
